import { useState, type FormEvent } from 'react'
import { createUserWithEmailAndPassword } from 'firebase/auth'
import { ref, set } from 'firebase/database'
import { auth, db } from '../firebase'
import { toast } from '../lib/toast'

export default function RegisterPage({
  onAuthed,
  onBackToLogin,
  onBack,
}: {
  onAuthed: () => void
  onBackToLogin: () => void
  onBack?: () => void
}) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [showPw, setShowPw] = useState(false)

  const validate = (): boolean => {
    const errs: Record<string, string> = {}
    if (!name.trim()) errs.name = 'El nombre es obligatorio'
    if (!email.trim()) errs.email = 'El email es obligatorio'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = 'Email inválido'
    if (!password) errs.password = 'La contraseña es obligatoria'
    else if (password.length < 6) errs.password = 'Mínimo 6 caracteres'
    if (password !== confirmPassword) errs.confirmPassword = 'Las contraseñas no coinciden'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    setLoading(true)
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password)
      const uid = cred.user.uid
      const now = new Date()
      const trialEnd = new Date(now.getTime() + 14 * 86400000)

      await set(ref(db, `users/parents/${uid}`), {
        name: name.trim(),
        email: email.toLowerCase(),
        createdAt: now.toISOString(),
        billing: {
          plan: 'trial',
          status: 'active',
          trialStart: now.toISOString(),
          trialEnd: trialEnd.toISOString(),
        },
      })

      toast('🎉 Cuenta creada. ¡Bienvenido a KidSafe!', 'success')
      onAuthed()
    } catch (err: unknown) {
      const code = (err as { code?: string }).code
      if (code === 'auth/email-already-in-use') setErrors({ email: 'Este email ya está registrado' })
      else if (code === 'auth/weak-password') setErrors({ password: 'Mínimo 6 caracteres' })
      else if (code === 'auth/invalid-email') setErrors({ email: 'Email inválido' })
      else setErrors({ form: 'No se pudo crear la cuenta. Inténtalo de nuevo.' })
    } finally {
      setLoading(false)
    }
  }

  const inputCls = (hasError?: string) =>
    `w-full rounded-xl border px-4 py-3 text-sm outline-none transition focus:ring-2 ${
      hasError
        ? 'border-rose-400 focus:ring-rose-300'
        : 'border-slate-200 focus:border-brand-500 focus:ring-brand-200'
    }`

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-brand-600 via-brand-500 to-brand-700 p-4">
      <div className="w-full max-w-md">
        <div className="rounded-3xl bg-white p-8 shadow-2xl">
          <div className="mb-6 text-center">
            {onBack && (
              <button
                onClick={onBack}
                className="mb-3 text-xs font-medium text-slate-400 hover:text-brand-600"
              >
                ← Volver al inicio
              </button>
            )}
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-600 text-2xl text-white">
              🛡️
            </div>
            <h1 className="text-2xl font-bold text-slate-800">Crear cuenta</h1>
            <p className="mt-1 text-sm text-slate-500">
              14 días gratis · Sin tarjeta de crédito
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Nombre completo</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Tu nombre"
                className={inputCls(errors.name)}
              />
              {errors.name && <p className="mt-1 text-xs text-rose-500">{errors.name}</p>}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Correo electrónico</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                className={inputCls(errors.email)}
              />
              {errors.email && <p className="mt-1 text-xs text-rose-500">{errors.email}</p>}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Contraseña</label>
              <div className="relative">
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className={`${inputCls(errors.password)} pr-12`}
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? 'Ocultar contraseñas' : 'Mostrar contraseñas'}
                  title={showPw ? 'Ocultar contraseñas' : 'Mostrar contraseñas'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-lg text-slate-400 hover:text-brand-600"
                >
                  {showPw ? '🙈' : '👁️'}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-xs text-rose-500">{errors.password}</p>}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Confirmar contraseña
              </label>
              <input
                type={showPw ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repite la contraseña"
                className={inputCls(errors.confirmPassword)}
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-xs text-rose-500">{errors.confirmPassword}</p>
              )}
            </div>

            {errors.form && (
              <div className="rounded-xl bg-rose-50 p-3 text-sm text-rose-600">{errors.form}</div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl bg-brand-600 py-3 font-semibold text-white transition hover:bg-brand-700 disabled:opacity-60"
            >
              {loading ? 'Creando cuenta…' : 'Crear cuenta gratis'}
            </button>

            <p className="text-center text-sm text-slate-500">
              ¿Ya tienes cuenta?{' '}
              <button
                type="button"
                onClick={onBackToLogin}
                className="font-semibold text-brand-600 hover:underline"
              >
                Inicia sesión
              </button>
            </p>
          </form>

          <div className="mt-6 grid grid-cols-3 gap-2 border-t border-slate-100 pt-4 text-center text-[11px] text-slate-400">
            <span>✓ 14 días gratis</span>
            <span>✓ Cancela cuando quieras</span>
            <span>✓ Sin anuncios</span>
          </div>
        </div>

        <button
          onClick={onBackToLogin}
          className="mt-4 w-full text-center text-sm text-white/80 hover:text-white"
        >
          ← Volver al inicio
        </button>
      </div>
    </div>
  )
}